<template>
  <div
    id="admin-popup-message-list"
    :dir="$functions.currentLangDirection()"
    class="popup-message-manager"
  >
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div
          class="header-text"
          style="text-align: right !important; direction: rtl !important;"
        >
          <h2
            class="page-title"
            style="direction: rtl !important; text-align: right !important;"
          >
            <v-icon
              color="#4F46E5"
              size="28"
              class="title-icon"
            >
              mdi-message-alert
            </v-icon>
            مدیریت پیام‌های پاپ‌آپ
          </h2>
          <p
            class="page-subtitle"
            style="text-align: right !important; direction: rtl !important;"
          >
            پیام‌های نمایش داده شده به کاربران را مدیریت کنید
          </p>
        </div>
        <div class="header-actions">
          <v-btn
            color="#4F46E5"
            dark
            large
            elevation="2"
            class="add-message-btn"
            @click="showModal"
          >
            <v-icon left>
              mdi-plus
            </v-icon>
            پیام جدید
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div
      class="stats-row"
      style="direction: rtl !important;"
    >
      <div
        class="stat-card active-messages"
        style="direction: rtl !important; text-align: right !important;"
      >
        <div class="stat-icon">
          <v-icon
            color="#10B981"
            size="24"
          >
            mdi-check-circle
          </v-icon>
        </div>
        <div
          class="stat-content"
          style="text-align: right !important;"
        >
          <div
            class="stat-number"
            style="text-align: right !important;"
          >
            {{ getActiveCount() }}
          </div>
          <div
            class="stat-label"
            style="text-align: right !important;"
          >
            پیام‌های فعال
          </div>
        </div>
      </div>
      <div
        class="stat-card total-messages"
        style="direction: rtl !important; text-align: right !important;"
      >
        <div class="stat-icon">
          <v-icon
            color="#3B82F6"
            size="24"
          >
            mdi-message-text
          </v-icon>
        </div>
        <div
          class="stat-content"
          style="text-align: right !important;"
        >
          <div
            class="stat-number"
            style="text-align: right !important;"
          >
            {{ (popupMessages || []).length }}
          </div>
          <div
            class="stat-label"
            style="text-align: right !important;"
          >
            کل پیام‌ها
          </div>
        </div>
      </div>
      <div
        class="stat-card high-priority"
        style="direction: rtl !important; text-align: right !important;"
      >
        <div class="stat-icon">
          <v-icon
            color="#EF4444"
            size="24"
          >
            mdi-alert-circle
          </v-icon>
        </div>
        <div
          class="stat-content"
          style="text-align: right !important;"
        >
          <div
            class="stat-number"
            style="text-align: right !important;"
          >
            {{ getHighPriorityCount() }}
          </div>
          <div
            class="stat-label"
            style="text-align: right !important;"
          >
            اولویت بالا
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Card -->
    <v-card
      class="main-content-card"
      elevation="3"
    >

      <!-- Messages Table -->
      <v-data-table
        :headers="headers"
        :items="filteredMessages"
        :loading="loading"
        loading-text="در حال بارگذاری..."
        :items-per-page="15"
        class="messages-table"
        :mobile-breakpoint="0"
      >
        <!-- Enhanced Status Column -->
        <template v-slot:item.isActive="{ item }">
          <div class="status-toggle">
            <label class="custom-switch">
              <input
                type="checkbox"
                :checked="item.isActive"
                @change="toggleStatus(item)"
              >
              <span class="slider"></span>
            </label>
            <span :class="['status-text', item.isActive ? 'active' : 'inactive']">
              {{ item.isActive ? 'فعال' : 'غیرفعال' }}
            </span>
          </div>
        </template>

        <!-- Enhanced Priority Column -->
        <template v-slot:item.priority="{ item }">
          <v-chip
            :color="getPriorityColor(item.priority)"
            dark
            small
            class="priority-chip"
          >
            <v-icon
              left
              size="14"
            >
              {{ getPriorityIcon(item.priority) }}
            </v-icon>
            {{ getPriorityText(item.priority) }}
          </v-chip>
        </template>

        <!-- Enhanced Message Column -->
        <template v-slot:item.message="{ item }">
          <div class="message-preview">
            <div class="message-title">
              {{ item.title }}
            </div>
            <div class="message-text">
              {{ item.message.length > 60 ? item.message.substring(0, 60) + '...' : item.message }}
            </div>
          </div>
        </template>


        <!-- Enhanced Date Column -->
        <template v-slot:item.showUntil="{ item }">
          <div class="date-info">
            <div
              v-if="item.showUntil"
              class="expiry-date"
            >
              <v-icon
                size="14"
                color="#6B7280"
              >
                mdi-calendar-clock
              </v-icon>
              {{ formatDate(item.showUntil) }}
            </div>
            <div
              v-else
              class="no-expiry"
            >
              <v-icon
                size="14"
                color="#10B981"
              >
                mdi-infinity
              </v-icon>
              <span>نامحدود</span>
            </div>
          </div>
        </template>

        <!-- Enhanced Actions Column -->
        <template v-slot:item.actions="{ item }">
          <div class="action-buttons">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="#3B82F6"
                  v-bind="attrs"
                  class="action-btn edit-btn"
                  v-on="on"
                  @click="onEditItem(item)"
                >
                  <v-icon size="18">
                    mdi-pencil
                  </v-icon>
                </v-btn>
              </template>
              <span>ویرایش پیام</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="#10B981"
                  v-bind="attrs"
                  class="action-btn preview-btn"
                  v-on="on"
                  @click="previewMessage(item)"
                >
                  <v-icon size="18">
                    mdi-eye
                  </v-icon>
                </v-btn>
              </template>
              <span>پیش‌نمایش</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="#EF4444"
                  v-bind="attrs"
                  class="action-btn delete-btn"
                  v-on="on"
                  @click="onDeleteItem(item)"
                >
                  <v-icon size="18">
                    mdi-delete
                  </v-icon>
                </v-btn>
              </template>
              <span>حذف پیام</span>
            </v-tooltip>
          </div>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="empty-state">
            <v-icon
              size="64"
              color="#D1D5DB"
            >
              mdi-message-alert-outline
            </v-icon>
            <h3>هیچ پیامی موجود نیست</h3>
            <p>برای شروع، اولین پیام خود را ایجاد کنید</p>
            <v-btn
              color="#4F46E5"
              dark
              @click="showModal"
            >
              <v-icon left>
                mdi-plus
              </v-icon>
              ایجاد پیام جدید
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Simple Modal for Add/Edit -->
    <v-dialog
      v-model="showAddEditModal"
      max-width="500px"
      persistent
    >
      <v-card
        class="simple-modal"
        style="direction: rtl !important;"
      >
        <v-card-title
          class="simple-header"
          style="direction: rtl !important; text-align: right !important; display: flex !important; align-items: center !important; padding: 16px !important;"
        >
          <v-btn
            icon
            small
            style="order: -1; margin-left: auto; margin-right: 0;"
            @click="closeModal"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <span style="direction: rtl !important; text-align: right !important; flex: 1; margin-right: 12px;">
            {{ editMode ? 'ویرایش پیام' : 'پیام جدید' }}
          </span>
        </v-card-title>

        <v-card-text
          class="simple-content"
          style="direction: rtl !important;"
        >
          <v-form
            ref="messageForm"
            v-model="formValid"
            lazy-validation
          >

            <div
              class="field-group"
              style="margin-bottom: 16px;"
            >
              <label style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #374151; text-align: right !important; direction: rtl !important;">عنوان پیام</label>
              <input
                v-model="currentMessage.title"
                type="text"
                placeholder="عنوان پیام را وارد کنید"
                style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; background-color: #f9fafb; direction: rtl !important; text-align: right !important; font-family: inherit;"
                @focus="$event.target.style.borderColor='#6366f1'; $event.target.style.backgroundColor='white'"
                @blur="$event.target.style.borderColor='#d1d5db'; $event.target.style.backgroundColor='#f9fafb'"
              >
            </div>

            <div
              class="field-group"
              style="margin-bottom: 16px;"
            >
              <label style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #374151; text-align: right !important; direction: rtl !important;">متن پیام</label>
              <textarea
                v-model="currentMessage.message"
                rows="3"
                placeholder="متن پیام را وارد کنید"
                style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; background-color: #f9fafb; direction: rtl !important; text-align: right !important; resize: vertical; font-family: inherit;"
                @focus="$event.target.style.borderColor='#6366f1'; $event.target.style.backgroundColor='white'"
                @blur="$event.target.style.borderColor='#d1d5db'; $event.target.style.backgroundColor='#f9fafb'"
              />
            </div>

            <div
              class="field-group"
              style="margin-bottom: 16px;"
            >
              <div style="margin-bottom: 12px;">
                <label>
                  <input
                    v-model="showButton"
                    type="checkbox"
                    style="margin-left: 8px;"
                  >
                  <span style="font-size: 14px; font-weight: 500; color: #374151;">نمایش دکمه در پاپ‌آپ</span>
                </label>
              </div>
              
              <div v-if="showButton">
                <label style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #374151; text-align: right !important; direction: rtl !important;">متن دکمه</label>
                <input
                  v-model="currentMessage.buttonText"
                  type="text"
                  placeholder="متن دکمه را وارد کنید"
                  style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; background-color: #f9fafb; direction: rtl !important; text-align: right !important; font-family: inherit;"
                  @focus="$event.target.style.borderColor='#6366f1'; $event.target.style.backgroundColor='white'"
                  @blur="$event.target.style.borderColor='#d1d5db'; $event.target.style.backgroundColor='#f9fafb'"
                >
              </div>
            </div>

            <div
              v-if="showButton"
              class="field-group"
              style="margin-bottom: 16px;"
            >
              <label style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #374151; text-align: right !important; direction: rtl !important;">عملکرد دکمه</label>
              <select
                v-model="currentMessage.buttonAction"
                style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; background-color: #f9fafb; direction: rtl !important; text-align: right !important; font-family: inherit; cursor: pointer;"
                @focus="$event.target.style.borderColor='#6366f1'; $event.target.style.backgroundColor='white'"
                @blur="$event.target.style.borderColor='#d1d5db'; $event.target.style.backgroundColor='#f9fafb'"
              >
                <option value="settings" style="direction: rtl; text-align: right;">
                  رفتن به تنظیمات
                </option>
                <option value="redirect" style="direction: rtl; text-align: right;">
                  هدایت به لینک
                </option>
                <option value="close" style="direction: rtl; text-align: right;">
                  بستن پاپ‌آپ
                </option>
              </select>
            </div>

            <div
              v-if="showButton && currentMessage.buttonAction === 'redirect'"
              class="field-group"
              style="margin-bottom: 16px;"
            >
              <label style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #374151; text-align: right !important; direction: rtl !important;">لینک هدایت</label>
              <input
                v-model="currentMessage.redirectUrl"
                type="text"
                placeholder="https://example.com"
                style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; background-color: #f9fafb; direction: ltr !important; text-align: left !important; font-family: inherit;"
                @focus="$event.target.style.borderColor='#6366f1'; $event.target.style.backgroundColor='white'"
                @blur="$event.target.style.borderColor='#d1d5db'; $event.target.style.backgroundColor='#f9fafb'"
              >
            </div>

            <div
              class="simple-row"
              style="direction: rtl !important; display: flex; gap: 16px; align-items: center;"
            >
              <div
                class="field-group"
                style="flex: 1;"
              >
                <label style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #374151; text-align: right !important; direction: rtl !important;">اولویت</label>
                <select
                  v-model="currentMessage.priority"
                  style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; background-color: #f9fafb; direction: rtl !important; text-align: right !important; font-family: inherit; cursor: pointer;"
                  @focus="$event.target.style.borderColor='#6366f1'; $event.target.style.backgroundColor='white'"
                  @blur="$event.target.style.borderColor='#d1d5db'; $event.target.style.backgroundColor='#f9fafb'"
                >
                  <option value="1" style="direction: rtl; text-align: right;">
                    کم
                  </option>
                  <option value="5" style="direction: rtl; text-align: right;">
                    متوسط
                  </option>
                  <option value="10" style="direction: rtl; text-align: right;">
                    بالا
                  </option>
                </select>
              </div>

              <div
                class="field-group"
                style="flex: 1;"
              >
                <div style="margin-bottom: 12px;">
                  <label>
                    <input
                      v-model="hasExpiry"
                      type="checkbox"
                      style="margin-left: 8px;"
                    >
                    <span style="font-size: 14px; font-weight: 500; color: #374151;">تاریخ انقضا دارد</span>
                  </label>
                </div>
                
                <div v-if="hasExpiry">
                  <label style="display: block; margin-bottom: 6px; font-size: 14px; font-weight: 500; color: #374151; text-align: right !important; direction: rtl !important;">انقضا بعد از (روز)</label>
                  <input
                    v-model="expiryDays"
                    type="number"
                    min="1"
                    max="365"
                    placeholder="1 تا 365 روز"
                    style="width: 100%; padding: 10px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 14px; background-color: #f9fafb; direction: rtl !important; text-align: right !important; font-family: inherit;"
                    @focus="$event.target.style.borderColor='#6366f1'; $event.target.style.backgroundColor='white'"
                    @blur="$event.target.style.borderColor='#d1d5db'; $event.target.style.backgroundColor='#f9fafb'"
                  >
                </div>
              </div>

              <div
                class="switch-group"
                style="display: flex; align-items: center; gap: 8px; margin-top: 20px;"
              >
                <span style="font-size: 14px; color: #374151;">فعال</span>
                <input
                  v-model="currentMessage.isActive"
                  type="checkbox"
                  class="simple-switch"
                >
              </div>
            </div>

          </v-form>
        </v-card-text>

        <v-card-actions
          class="simple-actions"
          style="direction: rtl !important;"
        >
          <v-btn
            color="#6366f1"
            dark
            :loading="submitting"
            :disabled="!formValid"
            style="margin-left: 8px;"
            @click="handleSubmit"
          >
            {{ editMode ? 'ذخیره' : 'ایجاد' }}
          </v-btn>
          <v-btn
            text
            @click="closeModal"
          >
            لغو
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Preview Modal -->
    <v-dialog
      v-model="showPreviewModal"
      max-width="500px"
    >
      <v-card class="preview-modal">
        <v-card-title class="preview-header">
          <v-icon
            color="#4F46E5"
            class="mr-2"
          >
            mdi-eye
          </v-icon>
          پیش‌نمایش پیام
        </v-card-title>
        <v-card-text class="preview-content">
          <div
            v-if="previewItem"
            class="preview-popup"
          >
            <div class="preview-notification-popup">
              <div class="preview-notification-content">
                <div class="preview-notification-header">
                  <div class="preview-notification-icon">
                    <v-icon color="#3B7AFF">
                      mdi-bell-ring-outline
                    </v-icon>
                  </div>
                  <div class="preview-notification-title">
                    {{ previewItem.title }}
                  </div>
                  <div class="preview-notification-close">
                    <v-icon size="16">
                      mdi-close
                    </v-icon>
                  </div>
                </div>
                <div class="preview-notification-body">
                  <p>{{ previewItem.message }}</p>
                </div>
                <div
                  v-if="previewItem.buttonText && previewItem.buttonText.trim()"
                  class="preview-notification-footer"
                >
                  <button class="preview-custom-button">
                    <v-icon
                      left
                      size="16"
                    >
                      {{ getButtonIcon(previewItem.buttonAction) }}
                    </v-icon>
                    <span>{{ previewItem.buttonText }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showPreviewModal = false"
          >
            بستن
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog
      v-model="showDeleteModal"
      max-width="400px"
    >
      <v-card class="delete-modal">
        <v-card-title class="delete-header">
          <v-icon
            color="#EF4444"
            class="mr-2"
          >
            mdi-alert-circle
          </v-icon>
          تایید حذف
        </v-card-title>
        <v-card-text>
          <p>آیا از حذف این پیام اطمینان دارید؟</p>
          <p class="text-caption text--secondary">
            این عمل غیرقابل بازگشت است.
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="showDeleteModal = false"
          >
            لغو
          </v-btn>
          <v-btn
            color="#EF4444"
            dark
            :loading="deleting"
            @click="confirmDelete"
          >
            حذف
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import ToastificationContent from '@core/components/toastification/ToastificationContent'
import popupMessageService from '@/services/popupMessage.service'

export default {
  name: 'PopupMessageList',
  data() {
    return {
      // Loading states
      loading: false,
      submitting: false,
      deleting: false,

      // Search and filters
      search: '',
      statusFilter: null,

      // Modal states
      showAddEditModal: false,
      showPreviewModal: false,
      showDeleteModal: false,

      // Data
      popupMessages: [],
      editMode: false,
      formValid: false,
      showButton: true,
      hasExpiry: false,
      expiryDays: 30,
      currentMessage: {
        id: null,
        title: '',
        message: '',
        buttonText: '',
        buttonAction: 'settings',
        redirectUrl: '',
        isActive: true,
        priority: 5,
        showUntil: '',
        createdBy: 'admin',
      },
      itemToDelete: null,
      previewItem: null,

      // Table configuration
      headers: [
        {
          text: 'پیام', value: 'message', align: 'right', width: '40%',
        },
        {
          text: 'اولویت', value: 'priority', align: 'center', width: '12%',
        },
        {
          text: 'وضعیت', value: 'isActive', align: 'center', width: '15%',
        },
        {
          text: 'تاریخ انقضا', value: 'showUntil', align: 'center', width: '18%',
        },
        {
          text: 'عملیات', value: 'actions', sortable: false, align: 'center', width: '15%',
        },
      ],

      // Form options
      buttonActionOptions: [
        { value: 'settings', text: 'رفتن به تنظیمات' },
        { value: 'redirect', text: 'هدایت به لینک' },
        { value: 'close', text: 'بستن پاپ‌آپ' },
      ],
      priorityOptions: [
        { value: 1, text: '1 - کم' },
        { value: 2, text: '2' },
        { value: 3, text: '3' },
        { value: 4, text: '4' },
        { value: 5, text: '5 - متوسط' },
        { value: 6, text: '6' },
        { value: 7, text: '7' },
        { value: 8, text: '8' },
        { value: 9, text: '9' },
        { value: 10, text: '10 - فوری' },
      ],
      audienceOptions: [
        { value: 'all', text: 'همه کاربران' },
        { value: 'new_users', text: 'کاربران جدید' },
        { value: 'existing_users', text: 'کاربران قدیمی' },
        { value: 'expired_users', text: 'کاربران منقضی' },
      ],
      statusFilterOptions: [
        { value: true, text: 'فعال' },
        { value: false, text: 'غیرفعال' },
      ],

      // Form validation rules
      titleRules: [
        v => !!v || 'عنوان الزامی است',
        v => (v && v.length <= 100) || 'عنوان نمی‌تواند بیش از 100 کاراکتر باشد',
      ],
      messageRules: [
        v => !!v || 'متن پیام الزامی است',
        v => (v && v.length <= 500) || 'متن پیام نمی‌تواند بیش از 500 کاراکتر باشد',
      ],
      buttonTextRules: [
        v => !!v || 'متن دکمه الزامی است',
        v => (v && v.length <= 50) || 'متن دکمه نمی‌تواند بیش از 50 کاراکتر باشد',
      ],
      urlRules: [
        v => !v || v.length <= 200 || 'لینک نمی‌تواند بیش از 200 کاراکتر باشد',
        v => !v || this.isValidUrl(v) || 'لطفا یک آدرس معتبر وارد کنید',
      ],
    }
  },

  computed: {
    isMobile() {
      return this.$store.state.appConfig.device === 'mobile'
    },

    filteredMessages() {
      let messages = [...this.popupMessages]

      if (this.statusFilter !== null) {
        messages = messages.filter(msg => msg.isActive === this.statusFilter)
      }

      return messages
    },
  },

  mounted() {
    this.checkAdminAuth()
  },

  methods: {
    checkAdminAuth() {
      const savedToken = localStorage.getItem('popup_admin_token')
      if (savedToken !== '876523') {
        this.$router.push('/admin/popup-message/auth')
        return
      }
      this.loadPopupMessages()
    },

    getEmptyMessage() {
      return {
        id: null,
        title: '',
        message: '',
        buttonText: '',
        buttonAction: 'settings',
        redirectUrl: '',
        isActive: true,
        priority: 5,
        showUntil: '',
        createdBy: 'admin',
      }
    },

    // Stats methods
    getActiveCount() {
      return (this.popupMessages || []).filter(msg => msg.isActive).length
    },

    getHighPriorityCount() {
      return (this.popupMessages || []).filter(msg => msg.priority >= 8).length
    },

    // Data loading
    async loadPopupMessages() {
      this.loading = true
      try {
        const response = await popupMessageService.getAll()
        if (response.status === 'success') {
          // Handle both array and object response
          if (Array.isArray(response.result)) {
            this.popupMessages = response.result
          } else if (response.result && Array.isArray(response.result.data)) {
            this.popupMessages = response.result.data
          } else {
            // If result is just a success message, set empty array
            this.popupMessages = []
          }
        } else {
          this.popupMessages = []
          this.showToast('خطا در بارگذاری پیام‌ها', 'error')
        }
      } catch (error) {
        // console.error('Error loading popup messages:', error)
        this.showToast('خطا در ارتباط با سرور', 'error')
      } finally {
        this.loading = false
      }
    },

    // Modal management
    showModal() {
      this.editMode = false
      this.currentMessage = this.getEmptyMessage()
      this.showButton = true
      this.hasExpiry = false
      this.expiryDays = 30
      this.formValid = false
      this.showAddEditModal = true
      this.$nextTick(() => {
        if (this.$refs.messageForm) {
          this.$refs.messageForm.resetValidation()
        }
      })
    },

    closeModal() {
      this.showAddEditModal = false
      this.currentMessage = this.getEmptyMessage()
      this.formValid = false
    },

    onEditItem(item) {
      this.editMode = true
      this.currentMessage = { ...item }
      this.showButton = !!(item.buttonText && item.buttonText.trim())
      this.hasExpiry = !!(item.showUntil)
      
      if (this.currentMessage.showUntil) {
        // Calculate days difference from now to showUntil
        const showUntilDate = new Date(this.currentMessage.showUntil)
        const now = new Date()
        const diffTime = showUntilDate - now
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        this.expiryDays = Math.max(1, diffDays)
      } else {
        this.expiryDays = 30
      }
      
      this.showAddEditModal = true
      this.$nextTick(() => {
        if (this.$refs.messageForm) {
          this.$refs.messageForm.resetValidation()
        }
      })
    },

    onDeleteItem(item) {
      this.itemToDelete = item
      this.showDeleteModal = true
    },

    previewMessage(item) {
      this.previewItem = item
      this.showPreviewModal = true
    },

    // Form handling
    async handleSubmit() {
      if (!this.$refs.messageForm.validate()) {
        return
      }

      this.submitting = true
      try {
        const messageData = { ...this.currentMessage }

        // Handle expiry date
        if (this.hasExpiry && this.expiryDays && this.expiryDays > 0) {
          const now = new Date()
          const expiryDate = new Date(now.getTime() + (this.expiryDays * 24 * 60 * 60 * 1000))
          messageData.showUntil = expiryDate.toISOString()
        } else {
          delete messageData.showUntil
        }

        if (!this.showButton) {
          messageData.buttonText = ''
          delete messageData.buttonAction
          delete messageData.redirectUrl
        }

        // Remove fields we don't want to send to backend
        delete messageData.targetAudience
        delete messageData.creationTime
        delete messageData.modificationTime
        delete messageData.__v

        let response
        if (this.editMode) {
          response = await popupMessageService.update(messageData)
        } else {
          response = await popupMessageService.create(messageData)
        }

        if (response.status === 'success') {
          this.showToast(
            this.editMode ? 'پیام با موفقیت ویرایش شد' : 'پیام با موفقیت ایجاد شد',
            'success',
          )
          this.closeModal()
          await this.loadPopupMessages()
        } else {
          this.showToast('خطا در ذخیره پیام', 'error')
        }
      } catch (error) {
        // console.error('Error saving popup message:', error)
        this.showToast('خطا در ارتباط با سرور', 'error')
      } finally {
        this.submitting = false
      }
    },

    async confirmDelete() {
      if (!this.itemToDelete) return

      this.deleting = true
      try {
        const response = await popupMessageService.delete(this.itemToDelete._id)
        if (response.status === 'success') {
          this.showToast('پیام با موفقیت حذف شد', 'success')
          this.showDeleteModal = false
          await this.loadPopupMessages()
        } else {
          this.showToast('خطا در حذف پیام', 'error')
        }
      } catch (error) {
        // console.error('Error deleting popup message:', error)
        this.showToast('خطا در ارتباط با سرور', 'error')
      } finally {
        this.deleting = false
        this.itemToDelete = null
      }
    },

    async toggleStatus(item) {
      // Toggle the state first (this happens immediately when checkbox is clicked)
      const newState = !item.isActive
      item.isActive = newState
      
      try {
        const response = await popupMessageService.toggleStatus(item._id, newState)
        if (response.status === 'success') {
          this.showToast('وضعیت پیام تغییر کرد', 'success')
          // Update the local array to ensure persistence
          const index = this.popupMessages.findIndex(msg => msg._id === item._id)
          if (index !== -1) {
            this.$set(this.popupMessages, index, { ...this.popupMessages[index], isActive: newState })
          }
        } else {
          // Rollback on failure
          item.isActive = !newState
          this.showToast('خطا در تغییر وضعیت', 'error')
        }
      } catch (error) {
        // console.error('Error toggling status:', error)
        // Rollback on error
        item.isActive = !newState
        this.showToast('خطا در ارتباط با سرور', 'error')
      }
    },

    // Filters
    applyFilters() {
      // Filters are applied through computed property
    },

    // Utility methods
    isValidUrl(string) {
      try {
        const url = new URL(string.startsWith('http') ? string : `https://${string}`)
        return url.protocol === 'http:' || url.protocol === 'https:'
      } catch (_) {
        return false
      }
    },

    getPriorityColor(priority) {
      if (priority <= 3) return '#10B981' // Green
      if (priority <= 6) return '#F59E0B' // Yellow
      return '#EF4444' // Red
    },

    getPriorityIcon(priority) {
      if (priority <= 3) return 'mdi-flag-outline'
      if (priority <= 6) return 'mdi-flag'
      return 'mdi-flag-variant'
    },

    getPriorityText(priority) {
      if (priority <= 3) return 'کم'
      if (priority <= 6) return 'متوسط'
      if (priority <= 9) return 'بالا'
      return 'فوری'
    },

    getAudienceColor(audience) {
      const colors = {
        all: '#6366F1',
        new_users: '#10B981',
        existing_users: '#3B82F6',
        expired_users: '#F59E0B',
      }
      return colors[audience] || '#6B7280'
    },

    getAudienceIcon(audience) {
      const icons = {
        all: 'mdi-account-group',
        new_users: 'mdi-account-plus',
        existing_users: 'mdi-account-check',
        expired_users: 'mdi-account-alert',
      }
      return icons[audience] || 'mdi-account'
    },

    getAudienceText(audience) {
      const texts = {
        all: 'همه',
        new_users: 'جدید',
        existing_users: 'قدیمی',
        expired_users: 'منقضی',
      }
      return texts[audience] || audience
    },

    getButtonIcon(buttonAction) {
      const iconMap = {
        settings: 'mdi-cog-outline',
        redirect: 'mdi-open-in-new',
        close: 'mdi-close',
      }
      return iconMap[buttonAction] || 'mdi-cog-outline'
    },

    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fa-IR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    showToast(message, variant = 'success') {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: variant === 'success' ? 'موفقیت' : variant === 'error' ? 'خطا' : 'هشدار',
          text: message,
          icon: variant === 'success' ? 'CheckIcon' : variant === 'error' ? 'XIcon' : 'AlertTriangleIcon',
          variant,
        },
      })
    },
  },
}
</script>

<style>
/* Force RTL with highest specificity to override global CSS */
html [dir="rtl"] #admin-popup-message-list,
html [dir="rtl"] #admin-popup-message-list *,
html [dir="rtl"] .popup-message-manager,
html [dir="rtl"] .popup-message-manager *,
html body #admin-popup-message-list,
html body #admin-popup-message-list *,
html body .popup-message-manager,
html body .popup-message-manager * {
  direction: rtl !important;
  text-align: right !important;
}

/* Override any LTR forcing */
html [dir="rtl"] #admin-popup-message-list {
  direction: rtl !important;
  text-align: right !important;
}

html [dir="rtl"] .popup-message-manager {
  direction: rtl !important;
  text-align: right !important;
}

/* Override specific problematic selectors */
html [dir="rtl"] .header-text,
html [dir="rtl"] .page-title,
html [dir="rtl"] .page-subtitle,
html [dir="rtl"] .stat-card,
html [dir="rtl"] .stat-content,
html [dir="rtl"] .stat-number,
html [dir="rtl"] .stat-label {
  direction: rtl !important;
  text-align: right !important;
}

/* Main Container */
.popup-message-manager {
  padding: 20px;
  background: linear-gradient(135deg, #f8faff 0%, #f1f5f9 100%);
  min-height: 100vh;
  direction: rtl !important;
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
  direction: rtl !important;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 20px;
  direction: rtl !important;
}

.header-text {
  text-align: right;
}

.page-title {
  color: #1f2937;
  font-size: 26px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  direction: rtl !important;
}

.title-icon {
  background: rgba(99, 102, 241, 0.1);
  padding: 8px;
  border-radius: 10px;
}

.page-subtitle {
  color: #6b7280;
  font-size: 15px;
  margin: 6px 0 0 0;
  font-weight: 400;
  text-align: right;
}

.add-message-btn {
  border-radius: 8px !important;
  font-weight: 600;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25) !important;
  transition: all 0.3s ease !important;
  background-color: #6366f1 !important;
  padding: 10px 20px !important;
  font-size: 14px !important;
}

.add-message-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35) !important;
  background-color: #5b5df1 !important;
}

/* Stats Row */
.stats-row {
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
  direction: rtl !important;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 200px;
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
  direction: rtl !important;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 16px;
  margin-left: 0;
  order: 2;
}

.stat-content {
  text-align: right;
  flex: 1;
  order: 1;
}

.active-messages .stat-icon {
  background: rgba(16, 185, 129, 0.1);
}

.total-messages .stat-icon {
  background: rgba(59, 130, 246, 0.1);
}

.high-priority .stat-icon {
  background: rgba(239, 68, 68, 0.1);
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
  text-align: right;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  text-align: right;
  line-height: 1.2;
}

/* Main Content Card */
.main-content-card {
  background: white !important;
  border-radius: 12px !important;
  overflow: hidden;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}


/* Data Table Enhancements */
.messages-table {
  direction: rtl !important;
}

.messages-table >>> .v-data-table__wrapper {
  direction: rtl !important;
}

.messages-table >>> th {
  text-align: right !important;
  background-color: #f1f5f9 !important;
  font-weight: 600 !important;
  color: #475569 !important;
  border-bottom: 1px solid #cbd5e1 !important;
  padding: 12px 16px !important;
  font-size: 13px !important;
  height: 48px !important;
}

.messages-table >>> td {
  text-align: right !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  font-size: 14px !important;
  vertical-align: middle !important;
}

.messages-table >>> tr:hover {
  background-color: #f8fafc !important;
}

/* Fix RTL alignment for form elements */
.messages-table >>> .v-input__control {
  direction: rtl !important;
}

.messages-table >>> .v-input__slot {
  direction: rtl !important;
}

/* Message Preview */
.message-preview {
  max-width: 300px;
}

.message-title {
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
  margin-bottom: 4px;
}

.message-text {
  color: #6b7280;
  font-size: 13px;
  line-height: 1.4;
}

/* Status Toggle */
.status-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: column;
}

/* Custom Switch */
.custom-switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
}

.custom-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #E5E7EB;
  transition: 0.3s;
  border-radius: 20px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

input:checked + .slider {
  background-color: #10B981;
}

input:focus + .slider {
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
}

input:checked + .slider:before {
  transform: translateX(20px);
}

.status-text {
  font-size: 11px;
  font-weight: 500;
  text-align: center;
}

.status-text.active {
  color: #10b981;
}

.status-text.inactive {
  color: #ef4444;
}

/* Priority & Audience Chips */
.priority-chip, .audience-chip {
  font-weight: 500 !important;
  border-radius: 6px !important;
  font-size: 12px !important;
  height: 24px !important;
}

/* Date Info */
.date-info {
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
  font-size: 13px;
}

.expiry-date {
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
}

.no-expiry {
  color: #10b981;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 500;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: 6px;
  justify-content: center;
  align-items: center;
}

.action-btn {
  transition: all 0.2s ease !important;
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
}

.action-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
}

.empty-state h3 {
  margin: 16px 0 8px 0;
  color: #374151;
  font-size: 18px;
  font-weight: 600;
}

.empty-state p {
  margin-bottom: 24px;
  font-size: 14px;
}

/* Modal Styles */
.add-edit-modal {
  border-radius: 16px !important;
  overflow: hidden;
  direction: rtl !important;
}

.modal-header {
  background: #6366f1 !important;
  color: white !important;
  padding: 20px 24px !important;
  border-bottom: none !important;
  direction: rtl !important;
}

.modal-title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
}

.close-btn {
  color: white !important;
}

.modal-content {
  padding: 24px !important;
  background: #fafbfc;
  direction: rtl !important;
}

/* Fix RTL for form elements in modal */
.add-edit-modal >>> .v-input__control {
  direction: rtl !important;
}

.add-edit-modal >>> .v-input__slot {
  direction: rtl !important;
}

.add-edit-modal >>> .v-text-field__slot {
  direction: rtl !important;
}

.add-edit-modal >>> .v-select__slot {
  direction: rtl !important;
}

.form-section {
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.section-title {
  color: #374151;
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.form-field {
  margin-bottom: 12px !important;
}

.form-field >>> .v-input__control {
  min-height: 48px !important;
}

.form-field >>> .v-text-field__details {
  margin-top: 4px !important;
  padding-top: 0 !important;
}

.form-field >>> .v-textarea .v-input__control {
  min-height: auto !important;
}

.form-switch {
  margin-top: 8px;
}

.modal-actions {
  background: #f8fafc !important;
  padding: 16px 24px !important;
  border-top: 1px solid #e2e8f0;
}

.cancel-btn {
  border-radius: 8px !important;
  font-weight: 500;
  padding: 10px 20px !important;
  color: #6b7280 !important;
  border-color: #d1d5db !important;
}

.submit-btn {
  border-radius: 8px !important;
  font-weight: 600;
  padding: 10px 20px !important;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.25) !important;
  background-color: #6366f1 !important;
}

/* Preview Modal */
.preview-modal {
  border-radius: 16px !important;
}

.preview-header {
  background: #f8faff !important;
  color: #1f2937 !important;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
}

.preview-content {
  padding: 24px !important;
  background: #f1f5f9;
}

.preview-notification-popup {
  max-width: 350px;
  margin: 0 auto;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.preview-notification-content {
  display: flex;
  flex-direction: column;
}

.preview-notification-header {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafbff;
}

.preview-notification-icon {
  margin-left: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(59, 122, 255, 0.1);
}

.preview-notification-title {
  flex-grow: 1;
  font-weight: 600;
  font-size: 14px;
  color: #1f2937;
}

.preview-notification-close {
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-notification-body {
  padding: 16px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.preview-notification-body p {
  margin: 0;
}

.preview-notification-footer {
  padding: 16px;
  padding-top: 0;
}

.preview-custom-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 40px;
  background-color: #4f46e5;
  color: white;
  border: none;
  border-radius: 10px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  gap: 8px;
}

.preview-custom-button:hover {
  background-color: #4338ca;
  transform: translateY(-1px);
}

/* Delete Modal */
.delete-modal {
  border-radius: 16px !important;
}

.delete-header {
  color: #ef4444 !important;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 768px) {
  .popup-message-manager {
    padding: 12px;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .stats-row {
    flex-direction: column;
    gap: 12px;
  }

  .stat-card {
    min-width: auto;
  }

  .search-container {
    flex-direction: column;
    gap: 12px;
  }

  .search-field, .status-filter {
    flex: none;
    min-width: auto;
    max-width: none;
  }

  .page-title {
    font-size: 22px;
  }

  .modal-content {
    padding: 16px !important;
  }

  .form-section {
    padding: 16px;
  }

  .action-buttons {
    gap: 4px;
  }

  .message-preview {
    max-width: 180px;
  }

  .add-message-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .stats-row {
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-number {
    font-size: 20px;
  }

  .modal-actions {
    padding: 16px 20px !important;
  }

  .preview-notification-popup {
    max-width: 280px;
  }
}

/* Animation Classes */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Simple Modal Styles */
.simple-modal {
  border-radius: 12px !important;
}

.simple-header {
  background: #f8f9fa !important;
  color: #374151 !important;
  padding: 16px 20px !important;
  border-bottom: 1px solid #e9ecef !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  font-weight: 600 !important;
  font-size: 18px !important;
}

.simple-content {
  padding: 20px !important;
  background: white !important;
}

.simple-field {
  margin-bottom: 16px !important;
}

.simple-field >>> .v-input__control {
  direction: rtl !important;
  text-align: right !important;
}

.simple-field >>> .v-input__slot {
  direction: rtl !important;
  text-align: right !important;
  background-color: #f8f9fa !important;
  border-radius: 6px !important;
}

.simple-field >>> .v-text-field__slot {
  direction: rtl !important;
  text-align: right !important;
}

.simple-field >>> .v-select__slot {
  direction: rtl !important;
  text-align: right !important;
}

/* Remove animations and fancy effects */
.simple-field >>> .v-input--is-focused .v-input__slot {
  background-color: #f1f3f4 !important;
  transform: none !important;
}

.simple-field >>> .v-label {
  transform: none !important;
  transition: none !important;
}

.simple-field >>> .v-text-field > .v-input__control > .v-input__slot:before {
  display: none !important;
}

.simple-field >>> .v-text-field > .v-input__control > .v-input__slot:after {
  display: none !important;
}

.simple-field >>> .v-select .v-select__selection {
  color: #374151 !important;
}

.simple-field >>> .v-input__append-inner {
  margin-top: 0 !important;
  align-self: center !important;
}

.simple-row {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
}

.simple-field-half {
  flex: 1;
  margin-bottom: 0 !important;
}

.simple-actions {
  padding: 16px 20px !important;
  border-top: 1px solid #e9ecef !important;
  background: #f8f9fa !important;
  display: flex !important;
  justify-content: flex-start !important;
  gap: 8px !important;
}

.simple-actions >>> .v-btn {
  min-width: 80px !important;
  height: 36px !important;
  font-size: 14px !important;
  border-radius: 6px !important;
}

/* Custom Form Fields */
.field-group {
  margin-bottom: 16px;
}

.field-label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  text-align: right !important;
  direction: rtl !important;
}

.simple-input, .simple-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background-color: #f9fafb;
  transition: border-color 0.2s ease;
  direction: rtl !important;
  text-align: right !important;
  font-family: inherit;
}

.simple-input:focus, .simple-select:focus {
  outline: none;
  border-color: #6366f1;
  background-color: white;
  box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.1);
}

.simple-input::placeholder {
  color: #9ca3af;
  direction: rtl !important;
  text-align: right !important;
}

.simple-select {
  cursor: pointer;
  background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>');
  background-repeat: no-repeat;
  background-position: left 12px center;
  background-size: 12px;
  padding-left: 32px;
}

.simple-select option {
  direction: rtl;
  text-align: right;
  padding: 8px;
}

.switch-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.simple-switch {
  width: 40px;
  height: 20px;
  position: relative;
  appearance: none;
  background-color: #d1d5db;
  border-radius: 20px;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.simple-switch:checked {
  background-color: #6366f1;
}

.simple-switch::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background-color: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.simple-switch:checked::before {
  transform: translateX(20px);
}

.simple-switch:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
}

/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
